import { ValidatedForm } from "../components/ValidatedForm";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { ApiError, request } from "../api/client";
import { ErrorNotice } from "../components/AdminFeedback";
import { useLocale } from "../i18n";

/** Retains an uncertain mutation verbatim until a retry or an authoritative reload. */
export function FinanceDialog({ title, description, path, payload, valid, onClose, onSaved, onReload, onResponse, children, requestIdRequired = true, submitLabel, method = "POST" }: {
  title: string; description: string; path: string; payload: unknown; valid: boolean;
  onClose: () => void; onSaved: () => void; onReload: () => void; children: ReactNode;
  onResponse?: (response: unknown, requestId: string) => "completed" | "pending";
  requestIdRequired?: boolean;
  submitLabel?: string;
  method?: "POST" | "PUT";
}) {
  const { w } = useLocale();
  const dialog = useRef<HTMLDialogElement>(null);
  const pending = useRef<unknown>(null);
  const running = useRef(false);
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [acceptedPending, setAcceptedPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const conflict = error instanceof ApiError && error.status === 409;
  const forbidden = error instanceof ApiError && error.status === 403;
  useEffect(() => { dialog.current?.showModal(); }, []);
  async function save() {
    if (running.current || conflict || forbidden || !valid) return;
    if (!pending.current) { const value = typeof payload === "function" ? payload() : payload; pending.current = { ...(value as object), ...(requestIdRequired ? { requestId: crypto.randomUUID() } : {}) }; }
    running.current = true; setBusy(true); setError(null);
    try {
      const response = await request(path, method, pending.current);
      if (onResponse?.(response, (pending.current as {requestId:string}).requestId) === "pending") {
        setAcceptedPending(true); setUncertain(false);
      } else { onSaved(); }
    }
    catch (cause) {
      setError(cause);
      const retryUncertain = !(cause instanceof ApiError) || cause.status >= 500 || cause.status === 408;
      setUncertain(retryUncertain);
      if (!retryUncertain && !acceptedPending) pending.current = null;
      if (cause instanceof ApiError && cause.status === 403) onReload();
    } finally { running.current = false; setBusy(false); }
  }
  return <dialog ref={dialog} className="action-dialog finance-dialog" aria-labelledby="finance-dialog-title" onCancel={event => { event.preventDefault(); if (!busy && !uncertain && !acceptedPending) onClose(); }}>
    <div className="dialog-header"><h2 id="finance-dialog-title">{title}</h2><button type="button" className="icon-button" disabled={busy || uncertain || acceptedPending} onClick={onClose} aria-label={w("إغلاق", "Close")}><XMarkIcon aria-hidden="true" /></button></div>
    <p className="finance-dialog-description">{description}</p>
    <ValidatedForm className="finance-form" valid={valid} onSubmit={event => { event.preventDefault(); void save(); }}>
      <fieldset disabled={busy || uncertain || acceptedPending || conflict || forbidden}>{children}</fieldset>
      <ErrorNotice error={error} />
      {uncertain && <p className="finance-conflict">{w("لم يصل تأكيد الحفظ. إعادة المحاولة ترسل العملية نفسها لتجنب التكرار. يمكنك أيضاً إعادة تحميل البيانات للتحقق من نتيجتها.", "Save confirmation was not received. Retry sends the same operation to avoid duplication. You can also reload to check its result.")}</p>}
      {acceptedPending && <p className="finance-conflict" role="status">{w("تم قبول العملية وما زالت قيد المعالجة. استأنف العملية نفسها للحصول على نتيجتها دون إنشاء طلب جديد.", "The operation was accepted and is still being processed. Resume the same operation to retrieve its result without creating another request.")}</p>}
      {conflict && <p className="finance-conflict">{w("تغيّرت الإعدادات منذ فتح هذا النموذج. أعد تحميلها وراجع التغيير قبل الحفظ مجدداً.", "These settings changed after this form was opened. Reload and review before saving again.")}</p>}
      <div className="finance-dialog-actions"><button type="button" className="entry-secondary" disabled={busy || uncertain || acceptedPending} onClick={onClose}>{w("إلغاء", "Cancel")}</button>
        {(conflict || uncertain) && <button type="button" className="entry-secondary" disabled={busy} onClick={onReload}>{w("إعادة تحميل البيانات", "Reload data")}</button>}
        {!conflict && !forbidden && <button className="entry-primary" disabled={busy}>{busy ? w("جارٍ الحفظ…", "Saving…") : acceptedPending ? w("استئناف العملية", "Resume operation") : uncertain ? w("إعادة محاولة الحفظ", "Retry save") : submitLabel ?? w("حفظ التغييرات", "Save changes")}</button>}
      </div>
    </ValidatedForm>
  </dialog>;
}
